import { MedplumProvider } from "@medplum/react-hooks";
import { ThemeProvider } from "@repo/ui/components/theme-provider";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { ChatSidebarProvider } from "./context/chat-sidebar-context";
import * as TanStackQueryProvider from "./integrations/tanstack-query/root-provider.tsx";
import { medplum } from "./lib/medplum";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

// Setup XState Inspector in development
// import { setupInspector } from "./lib/xstate-inspector";

// if (import.meta.env.DEV) {
//   setupInspector();
// }

// Create router
const TanStackQueryProviderContext = TanStackQueryProvider.getContext();
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
    // biome-ignore lint/style/noNonNullAssertion: context init placeholder, overridden at render
    medplum: undefined!,
  },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render after Medplum restores session
async function initApp() {
  await medplum.getInitPromise();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      medplum.refreshIfExpired().catch(() => {});
    }
  });

  // Wait for session restoration
  const activeLogin = medplum.getActiveLogin();
  if (activeLogin) {
    medplum.setAccessToken(activeLogin.accessToken, activeLogin.refreshToken);
    try {
      await medplum.getProfileAsync();
    } catch {
      // Profile fetch failed (stale token, deleted project, etc). Without an
      // explicit redirect, the app would render in a broken state and other
      // hooks would re-hit /auth/me in a loop. Clear tokens and bounce to
      // /login deterministically — do NOT rely on Medplum's onUnauthenticated
      // callback firing here, since it's not guaranteed for every 401 path.
      medplum.clear();
      if (window.location.pathname !== "/login") {
        window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
    }
  }

  const rootElement = document.getElementById("root");
  // No `!rootElement.innerHTML` guard: #root ships with the boot splash inside it
  // (index.html), which createRoot clears on first render. The guard read a
  // non-empty container as "already mounted" and skipped rendering forever.
  // initApp() is invoked exactly once at module scope, so there is nothing to
  // guard against — provider and patient-portal have never had the check.
  if (rootElement) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
          <ThemeProvider defaultTheme="dark" storageKey="ai-theme">
            <MedplumProvider medplum={medplum}>
              <ChatSidebarProvider>
                <RouterProvider router={router} context={{ medplum }} />
              </ChatSidebarProvider>
            </MedplumProvider>
          </ThemeProvider>
        </TanStackQueryProvider.Provider>
      </StrictMode>,
    );
  }
}

initApp().catch(console.error);
